import React, { useState } from 'react';
import { MailIcon, PhoneIcon } from 'lucide-react';
import { motion } from 'framer-motion';
const MAIL_ENDPOINT = 'https://scan.noorddigital.nl/mail.php';
export const ContactForm: React.FC = () => {
  const [form, setForm] = useState({
    voornaam: '',
    achternaam: '',
    email: '',
    telefoon: '',
    bericht: ''
  });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setForm({
      ...form,
      [e.target.name]: e.target.value
    });
  }
  async function handleSubmit() {
    if (!form.voornaam.trim() || !form.email.trim() || !form.telefoon.trim() || !form.bericht.trim()) {
      setErrorMsg('Vul alle verplichte velden in.');
      return;
    }
    setErrorMsg('');
    setStatus('sending');
    try {
      const body = new URLSearchParams({
        naam: `${form.voornaam.trim()} ${form.achternaam.trim()}`.trim(),
        email: form.email.trim(),
        telefoon: form.telefoon.trim(),
        bericht: form.bericht.trim(),
        ontvanger: 'info@noorddigital.nl',
        onderwerp: `Nieuw contactformulier Noord Digital — ${form.voornaam.trim()}`
      });
      const res = await fetch(MAIL_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: body.toString()
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus('success');
    } catch (e: any) {
      setStatus('error');
      setErrorMsg('Versturen mislukt. Probeer opnieuw of mail info@noorddigital.nl.');
    }
  }
  return <section id="contact" className="w-full bg-gradient-to-r from-[#007AFF] to-[#1B2B44] py-20 md:py-32 relative overflow-hidden" data-id="element-141">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] relative z-10" data-id="element-142">
        {/* Header */}
        <div className="mb-16 max-w-3xl" data-id="element-143">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-144">
            Klaar voor de volgende stap in online groei?
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-white/90 text-lg" data-id="element-145">
            Plan een vrijblijvend gesprek! We bekijken samen welke aanpak past bij jouw bedrijf en groeifase.
          </motion.p>
        </div>

        {/* Card Container */}
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.2
      }} className="bg-white rounded-3xl overflow-hidden flex flex-col lg:flex-row shadow-2xl" data-id="element-146">
          {/* Left Column */}
          <div className="bg-[#EBF3FF] p-8 md:p-12 lg:w-5/12 flex flex-col" data-id="element-147">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/hNQXPwX8APxFUETyhYjjKE/Mogelijk_voor_website_%2837%29.jpg" alt="Rocco" className="w-full aspect-[4/5] object-cover rounded-2xl mb-8 shadow-sm border border-white/50" data-id="element-148" />
            <h3 className="text-4xl font-bold text-[#111827] mb-4" data-id="element-149">Geïnteresseerd?</h3>
            <p className="text-[#4B5563] mb-8 leading-relaxed" data-id="element-150">
              Vul het formulier in en we nemen snel contact met je op om je project te bespreken.
            </p>
            <div className="space-y-4 mb-12" data-id="element-151">
              <div className="flex items-center gap-4" data-id="element-152">
                <div className="w-10 h-10 rounded-full bg-[#007AFF] flex items-center justify-center flex-shrink-0" data-id="element-153">
                  <MailIcon className="w-5 h-5 text-white" data-id="element-154" />
                </div>
                <a href="mailto:info@noorddigital.nl" className="text-[#111827] font-medium hover:text-[#007AFF] transition-colors" data-id="element-155">
                  info@noorddigital.nl
                </a>
              </div>
              <div className="flex items-center gap-4" data-id="element-156">
                <div className="w-10 h-10 rounded-full bg-[#007AFF] flex items-center justify-center flex-shrink-0" data-id="element-157">
                  <PhoneIcon className="w-5 h-5 text-white" data-id="element-158" />
                </div>
                <a href="tel:+31653689686" className="text-[#111827] font-medium hover:text-[#007AFF] transition-colors" data-id="element-159">
                  +31 6 53689686
                </a>
              </div>
            </div>
            <p className="text-[10px] text-[#6B7280] mt-auto leading-tight" data-id="element-160">
              Uw gegevens zijn bij ons veilig en worden uitsluitend gebruikt om u te helpen naar aanleiding van dit formulier
            </p>
          </div>

          {/* Right Column */}
          <div className="bg-white p-8 md:p-12 lg:w-7/12 relative" data-id="element-161">
            {/* Dots */}
            <div className="absolute top-8 right-8 flex gap-1.5" data-id="element-162">
              {['#004AC7', '#007AFF', '#66A3FF'].map((color, i) => <motion.div key={color} className="w-3 h-3 rounded-full" style={{
              backgroundColor: color
            }} animate={{
              y: [0, -6, 0],
              opacity: [0.6, 1, 0.6]
            }} transition={{
              duration: 1.6,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: i * 0.2
            }} data-id="element-163" />)}
            </div>

            {status === 'success' ? <div className="flex flex-col items-center justify-center h-full py-16 text-center" data-id="element-164">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6" data-id="element-165">
                  <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-166">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-167" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-[#111827] mb-3" data-id="element-168">Bericht verzonden!</h3>
                <p className="text-[#4B5563]" data-id="element-169">We nemen zo snel mogelijk contact met je op.</p>
              </div> : <div className="space-y-6 mt-4 md:mt-0" data-id="element-170">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-171">
                  <div data-id="element-172">
                    <label className="block text-sm font-medium text-[#4B5563] mb-2" data-id="element-173">
                      Voornaam <span className="text-red-500" data-id="element-174">*</span>
                    </label>
                    <input type="text" name="voornaam" value={form.voornaam} onChange={handleChange} className="w-full bg-[#F7F8FA] border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-transparent transition-all" placeholder="Voornaam" data-id="element-175" />
                  </div>
                  <div data-id="element-176">
                    <label className="block text-sm font-medium text-[#4B5563] mb-2" data-id="element-177">
                      Achternaam
                    </label>
                    <input type="text" name="achternaam" value={form.achternaam} onChange={handleChange} className="w-full bg-[#F7F8FA] border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-transparent transition-all" placeholder="Achternaam" data-id="element-178" />
                  </div>
                </div>

                <div data-id="element-179">
                  <label className="block text-sm font-medium text-[#4B5563] mb-2" data-id="element-180">
                    Email <span className="text-red-500" data-id="element-181">*</span>
                  </label>
                  <input type="email" name="email" value={form.email} onChange={handleChange} className="w-full bg-[#F7F8FA] border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-transparent transition-all" placeholder="je.naam@voorbeeld.nl" data-id="element-182" />
                </div>

                <div data-id="element-183">
                  <label className="block text-sm font-medium text-[#4B5563] mb-2" data-id="element-184">
                    Je telefoonnummer <span className="text-red-500" data-id="element-185">*</span>
                  </label>
                  <input type="tel" name="telefoon" value={form.telefoon} onChange={handleChange} className="w-full bg-[#F7F8FA] border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-transparent transition-all" placeholder="06 53689686" data-id="element-186" />
                </div>

                <div data-id="element-187">
                  <label className="block text-sm font-medium text-[#4B5563] mb-2" data-id="element-188">
                    Beschrijf wat we voor je kunnen doen <span className="text-red-500" data-id="element-189">*</span>
                  </label>
                  <textarea name="bericht" rows={4} value={form.bericht} onChange={handleChange} className="w-full bg-[#F7F8FA] border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-transparent transition-all resize-none" placeholder="Vertel wat we voor je kunnen betekenen..." data-id="element-190" />
                </div>

                {errorMsg && <p className="text-red-500 text-sm" data-id="element-191">{errorMsg}</p>}

                <button type="button" onClick={handleSubmit} disabled={status === 'sending'} className="w-full bg-[#007AFF] hover:bg-[#005FCC] disabled:opacity-60 text-white font-semibold py-4 rounded-xl transition-colors text-lg mt-4" data-id="element-192">
                  {status === 'sending' ? 'Versturen...' : 'Verstuur'}
                </button>
              </div>}
          </div>
        </motion.div>
      </div>
    </section>;
};